/**
 * MonthGrid: the Monday-first month grid, one cell per day, dots per record, the
 * period shading behind them. A cell with records is a button that opens the day
 * (ADR-026: hand-rolled, no date library).
 *
 * Author: Mahmoud Ragab Ahmed.
 */

import { strings } from "../brand/strings";

import { markClass, shadeClass, type CalendarCell } from "./model";

export function MonthGrid({
  cells,
  onSelectDay,
  compact = false,
}: {
  /** A smaller cell, for the quarter's three months side by side (wave W2). */
  compact?: boolean;
  /** The month's cells, from monthCells() with the day's marks folded in. */
  cells: readonly (CalendarCell & { readonly year: number; readonly month: number })[];
  /** Receives the ISO date of a clicked cell that carries records. */
  onSelectDay: (iso: string) => void;
}): React.JSX.Element {
  return (
    <div
      className={compact ? "q-cal__grid q-cal__grid--compact" : "q-cal__grid"}
      data-testid="month-grid"
      role="grid"
      aria-label={strings.calendar.heading}
    >
      {strings.calendar.weekdayNames.map((weekday) => (
        <div
          key={weekday}
          role="columnheader"
          className="q-cal__cell q-cal__cell--head"
          style={{ minHeight: 0 }}
        >
          <span className="q-cal__date">{weekday}</span>
        </div>
      ))}
      {cells.map((cell) => {
        const count = cell.dots.length;
        const dayLabel =
          count === 0 ? cell.iso : `${cell.iso}, ${strings.calendar.dayCount(count)}`;
        return (
          <div
            key={cell.iso}
            role="gridcell"
            className={`q-cal__cell${cell.inMonth ? "" : " q-cal__cell--out"}${shadeClass(cell.shade)}`}
          >
            {count > 0 ? (
              <button
                type="button"
                className="q-cal__day"
                aria-label={dayLabel}
                onClick={() => {
                  onSelectDay(cell.iso);
                }}
              >
                <span className={`q-cal__date${cell.inMonth ? "" : " q-cal__date--out"}`}>
                  {String(cell.day)}
                </span>
                <span className="q-cal__dots">
                  {cell.dots.map((dot, index) => (
                    <span
                      key={index}
                      className={markClass(dot)}
                      aria-hidden="true"
                      title={dot.label}
                    />
                  ))}
                </span>
              </button>
            ) : (
              <span className={`q-cal__date${cell.inMonth ? "" : " q-cal__date--out"}`}>
                {String(cell.day)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
